import { t } from '@lingui/core/macro'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import type { SendMessageRequest } from 'features/chatbot/types/api'
import type { ChatStreamEvent } from 'features/chatbot/types/stream'

import type { ChatApi } from './chatApi'

interface RawSSEData {
  type?: string
  seq?: number
  stream_id?: string
  session_id?: string
  message_id?: string
  thought_id?: string
  text?: string
  name?: string
  args?: Record<string, unknown>
  summary?: string
  references?: {
    documents: RawRef[]
    instruments: RawInstrumentRefSSE[]
    units: RawUnitRefSSE[]
  }
  documents?: RawRef[]
  instruments?: RawInstrumentRefSSE[]
  units?: RawUnitRefSSE[]
  chunk?: string
  base64?: string
  mime_type?: string
  chart_info?: Record<string, unknown>
  persisted_through_seq?: number
  content?: string
  all_documents?: RawRef[]
  all_instruments?: RawInstrumentRefSSE[]
  all_units?: RawUnitRefSSE[]
  cost_info?: {
    input_tokens: number
    output_tokens: number
    thinking_tokens: number
    cost_usd: number
  }
  thinking_time_seconds?: number
  error?: string
  details?: string
}

interface RawRef {
  id: string
  title: string
  drawing_number?: string
  document_type?: string
  page_number?: number
}

interface RawInstrumentRefSSE {
  id: string
  instrument_name: string
  pi_tag?: string
  unit_id?: string
  start_datetime?: string
  end_datetime?: string
}

interface RawUnitRefSSE {
  id: string
  name: string
  site_id: string
}

function mapDocRefs(raw: RawRef[] | undefined) {
  return (raw ?? []).map((d) => ({
    id: d.id,
    title: d.title,
    drawingNumber: d.drawing_number,
    documentType: d.document_type,
    pageNumber: d.page_number,
  }))
}

function mapInstRefs(raw: RawInstrumentRefSSE[] | undefined) {
  return (raw ?? []).map((i) => ({
    id: i.id,
    instrumentName: i.instrument_name,
    piTag: i.pi_tag,
    unitId: i.unit_id,
    startDatetime: i.start_datetime,
    endDatetime: i.end_datetime,
  }))
}

function mapUnitRefs(raw: RawUnitRefSSE[] | undefined) {
  return (raw ?? []).map((u) => ({
    id: u.id,
    name: u.name,
    siteId: u.site_id,
  }))
}

function parseSSEEvent(data: RawSSEData): ChatStreamEvent | null {
  const seq = data.seq ?? 0
  const messageId = data.message_id
  const thoughtId = data.thought_id

  switch (data.type) {
    case 'stream_started':
      return {
        type: 'stream_started',
        seq: 0 as const,
        streamId: data.stream_id ?? '',
        sessionId: data.session_id ?? '',
        messageId: data.message_id ?? '',
      }
    case 'thinking':
      return {
        type: 'thinking',
        seq,
        text: data.text ?? '',
        messageId,
        thoughtId,
      }
    case 'tool_call':
      return {
        type: 'tool_call',
        seq,
        name: data.name ?? '',
        args: data.args ?? {},
        messageId,
        thoughtId,
      }
    case 'tool_result':
      return {
        type: 'tool_result',
        seq,
        name: data.name ?? '',
        summary: data.summary ?? '',
        references: {
          documents: mapDocRefs(data.references?.documents),
          instruments: mapInstRefs(data.references?.instruments),
          units: mapUnitRefs(data.references?.units),
        },
        content:
          data.content && typeof data.content === 'object'
            ? (data.content as Record<string, unknown>)
            : undefined,
        messageId,
        thoughtId,
      }
    case 'references_updated':
      return {
        type: 'references_updated',
        seq,
        documents: mapDocRefs(data.documents),
        instruments: mapInstRefs(data.instruments),
        units: mapUnitRefs(data.units),
        messageId,
        thoughtId,
      }
    case 'text':
      return { type: 'text', seq, chunk: data.chunk ?? '', messageId }
    case 'image':
      return {
        type: 'image',
        seq,
        base64: data.base64 ?? '',
        mimeType: data.mime_type ?? 'image/png',
        chartInfo: data.chart_info ?? {},
        messageId,
      }
    case 'progress':
      return {
        type: 'progress',
        seq,
        persistedThroughSeq: data.persisted_through_seq ?? 0,
        messageId,
      }
    case 'done':
      return {
        type: 'done',
        seq,
        messageId: data.message_id ?? '',
        content: data.content ?? '',
        allDocuments: mapDocRefs(data.all_documents),
        allInstruments: mapInstRefs(data.all_instruments),
        allUnits: mapUnitRefs(data.all_units),
        costInfo: data.cost_info
          ? {
              inputTokens: data.cost_info.input_tokens,
              outputTokens: data.cost_info.output_tokens,
              thinkingTokens: data.cost_info.thinking_tokens,
              costUsd: data.cost_info.cost_usd,
            }
          : { inputTokens: 0, outputTokens: 0, thinkingTokens: 0, costUsd: 0 },
        thinkingTimeSeconds: data.thinking_time_seconds ?? 0,
      }
    case 'error':
      return {
        type: 'error',
        seq,
        error: data.error ?? t`Unknown error`,
        details: data.details,
      }
    default:
      return null
  }
}

export interface SSEClientCallbacks {
  onEvent: (event: ChatStreamEvent) => void
  onError: (error: string) => void
  onComplete: () => void
  /** Stream ended without a terminal done/error event; attempt recovery. Falls back to onComplete. */
  onDisconnect?: () => void
}

export interface SSEConnection {
  abort: () => void
}

export function connectToStream(
  api: ChatApi,
  sessionId: string,
  request: SendMessageRequest,
  callbacks: SSEClientCallbacks,
  initialSeq = -1
): SSEConnection {
  const abortController = new AbortController()
  let lastProcessedSeq = initialSeq
  let sawTerminalEvent = false

  const url = api.getSendMessageUrl(sessionId)

  fetchEventSource(url, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: {
      'Content-Type': 'application/json',
      Authorization: api.authorization,
    },
    signal: abortController.signal,
    openWhenHidden: true,
    onmessage(rawEvent) {
      let data: RawSSEData
      try {
        data = JSON.parse(rawEvent.data) as RawSSEData
      } catch {
        return
      }

      if (rawEvent.event) {
        data.type = rawEvent.event
      }

      const event = parseSSEEvent(data)
      if (!event) return

      if (event.seq <= lastProcessedSeq) return
      lastProcessedSeq = event.seq

      if (event.type === 'done' || event.type === 'error') {
        sawTerminalEvent = true
      }

      callbacks.onEvent(event)

      if (event.type === 'done') {
        callbacks.onComplete()
      }
    },
    onerror(error) {
      if (error?.name !== 'AbortError') {
        if (sawTerminalEvent) {
          callbacks.onComplete()
        } else if (callbacks.onDisconnect) {
          callbacks.onDisconnect()
        } else {
          callbacks.onError(
            (error as Error | undefined)?.message ?? t`SSE connection error`
          )
        }
      }
      throw error ?? new Error('SSE connection error')
    },
    onclose() {
      if (sawTerminalEvent || !callbacks.onDisconnect) {
        callbacks.onComplete()
      } else {
        callbacks.onDisconnect()
      }
    },
  }).catch(() => undefined)

  return {
    abort: () => abortController.abort(),
  }
}

export function reconnectToStream(
  api: ChatApi,
  streamId: string,
  lastSeq: number,
  sessionId: string | null,
  callbacks: SSEClientCallbacks
): SSEConnection & { gonePromise: Promise<boolean> } {
  const abortController = new AbortController()
  let lastProcessedSeq = lastSeq
  let sawTerminalEvent = false
  let resolveGone: (gone: boolean) => void = () => {}
  const gonePromise = new Promise<boolean>((resolve) => {
    resolveGone = resolve
  })

  const url = api.getReconnectUrl(streamId)

  void (async () => {
    let response: Response
    try {
      response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ last_seq: lastSeq, session_id: sessionId }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: api.authorization,
          Accept: 'text/event-stream',
        },
        signal: abortController.signal,
      })
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        resolveGone(false)
        return
      }
      resolveGone(false)
      callbacks.onError((error as Error).message ?? t`Reconnection error`)
      return
    }

    if (response.status === 410) {
      resolveGone(true)
      return
    }
    if (!response.ok) {
      resolveGone(false)
      const status = response.status
      callbacks.onError(t`Reconnect failed: HTTP ${status}`)
      return
    }

    resolveGone(false)
    const body = response.body
    if (!body) {
      callbacks.onComplete()
      return
    }

    const reader = body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    const dispatch = (rawEvent: { event?: string; data: string }) => {
      let data: RawSSEData
      try {
        data = JSON.parse(rawEvent.data) as RawSSEData
      } catch {
        return
      }
      if (rawEvent.event) {
        data.type = rawEvent.event
      }
      const event = parseSSEEvent(data)
      if (!event) return
      if (event.seq <= lastProcessedSeq) return
      lastProcessedSeq = event.seq
      if (event.type === 'done' || event.type === 'error') {
        sawTerminalEvent = true
      }
      callbacks.onEvent(event)
      if (event.type === 'done') {
        callbacks.onComplete()
      }
    }

    const flushBuffer = () => {
      let boundary = buffer.indexOf('\n\n')
      while (boundary !== -1) {
        const raw = buffer.slice(0, boundary)
        buffer = buffer.slice(boundary + 2)
        let event: string | undefined
        let data = ''
        for (const line of raw.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim()
          else if (line.startsWith('data:')) data += line.slice(5).trim()
        }
        if (data) dispatch({ event, data })
        boundary = buffer.indexOf('\n\n')
      }
    }

    try {
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        flushBuffer()
      }
      buffer += decoder.decode()
      flushBuffer()
      if (sawTerminalEvent || !callbacks.onDisconnect) {
        callbacks.onComplete()
      } else {
        callbacks.onDisconnect()
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') return
      if (!sawTerminalEvent && callbacks.onDisconnect) {
        callbacks.onDisconnect()
        return
      }
      callbacks.onError(
        (error as Error).message ?? t`Reconnection stream error`
      )
    }
  })()

  return {
    abort: () => abortController.abort(),
    gonePromise,
  }
}
